import { useState, useEffect, useContext } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentIntent, StripeCardElement } from "@stripe/stripe-js"
import { Button, Checkbox, CircularProgress } from '@material-ui/core';

import './CheckOut.scss';
import { MENTAL_CAPACITY_ANALYSIS_PRICE, PURCHASE_PRICE } from '../../config';
import { Authorization } from '../../services/authorization';
import { LANDING_URL } from '../../config';

import { useAppSelector } from '../../app/hooks';
import { selectAffiliate } from '../../features/affiliate/affiliateSlice';
import LifeTimeUpdate, { LifeTimeUpdates } from '../baseComponents/LifeTimeUpdate';
import { GetEmail, GetRecordingToken } from '../../services/localStorage/localstorage';
import {
  checkRecordingToken, validateVoucher,
  updateUser
} from '../../services/axios/requests';
import { Voucher } from '../../models/voucher';
import { createPaymentIntent, paymentSubscription, syncSubscription, waitForPaymentConfirmation } from '../../services/paymentService';
import CurrentRecordingContext from '../../contexts/currentRecordingContext';
import OrderContext from '../../contexts/orderContext';
import CurrentUserContext from '../../contexts/currentUserContext';
import { useHistory } from 'react-router-dom';
import { trackPurchase } from '../../services/analyticsService';

interface CheckOutProps {
  back(): void;
  next(): void;
};

interface PaymentResult {
  success: boolean;
  // Set only when Stripe took a payment; absent for a £0 card setup.
  paymentIntent?: PaymentIntent;
};

const CheckOut = (props: CheckOutProps) => {
  const history = useHistory();
  const currentOrder = useContext(OrderContext);
  const [affiliatePrePaidToken, setAffiliatePrePaidToken] = useState(null as string | null);
  const [isUserLifetimeSubscriptionActive, setIsUserLifetimeSubscriptionActive] = useState(null as boolean | null);

  const [nextButtonSubmitting, setNextButtonSubmitting] = useState(false);
  // Set while the backend is asked whether the payment has been recorded.
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  // Set when the order went through but the backend had not recorded it as paid within the wait.
  const [paymentPending, setPaymentPending] = useState(false);

  const [discountApplied, setDiscountApplied] = useState("");
  const [snapshotPrice, setSnapshotPrice] = useState(PURCHASE_PRICE);
  const [totalPrice, setTotalPrice] = useState(PURCHASE_PRICE);

  const stripe = useStripe();
  const elements = useElements();
  const affiliate = useAppSelector(selectAffiliate);

  const currentRecording = useContext(CurrentRecordingContext);
  const currentUser = useContext(CurrentUserContext);

  const back = async (e: any) => {
    props.back();
  }

  const next = async (e: any) => {
    if (e)
      e.preventDefault();

    setNextButtonSubmitting(true);

    try {
      // Free checkouts go through the backend too: it records the transaction, keeps the voucher
      // and marks the recording paid, then reports that there is nothing to charge.
      const res = await handlePayment();
      if (!res.success) {
        setNextButtonSubmitting(false);
        return false;
      }

      if (res.paymentIntent)
        await trackPurchase({
          transactionId: res.paymentIntent.id,
          // Stripe amounts are in pence.
          value: res.paymentIntent.amount / 100,
          currency: res.paymentIntent.currency.toUpperCase(),
          coupon: getCoupon(),
          snapshotPrice: parseFloat(snapshotPrice),
          addAnalysis: currentOrder.CurrentOrder.addAnalysis,
          addLifetimeUpdates: currentOrder.CurrentOrder.addLifetimeUpdates,
          // Hashed before it leaves the browser, for Google Ads enhanced conversions.
          email: currentUser.User?.email ?? GetEmail()
        });
      await updateUser({ signupComplete: true });

      // Card payments are recorded by the Stripe webhook, so check that it has landed before
      // moving on. A free checkout was recorded synchronously and confirms on the first poll.
      setConfirmingPayment(true);
      const confirmation = await waitForPaymentConfirmation(currentRecording.VideoRecording!.videoId);
      setConfirmingPayment(false);
      setNextButtonSubmitting(false);

      if (confirmation === 'confirmed') {
        props.next();
        return true;
      }

      // Stripe has confirmed the charge, so never ask for a second payment here.
      setPaymentPending(true);
      return false;
    } catch (err: any) {
      console.error(err);
      const message = err?.response?.data?.error;
      alert(message ? message : 'There is an issue with payment. Please try again later.');
      setConfirmingPayment(false);
      setNextButtonSubmitting(false);
      return false;
    }
  }

  const validateRecordingToken = async () => {
    const token = GetRecordingToken();
    if (token) {
      const res = await checkRecordingToken(token);
      if (res && res.status === 200 && res.data) {
        setAffiliatePrePaidToken(token);
        return true;
      }
    }
    return false;
  }

  useEffect(() => {
    const calculateOrder = async () => {

      var haveSubscription = await Authorization.GetUserSubscriptionStatus() === true;
      setIsUserLifetimeSubscriptionActive(haveSubscription);

      var haveValidSnapshotToken = await validateRecordingToken();

      var basePurchasePrice = haveSubscription || haveValidSnapshotToken ? 0 : parseFloat(PURCHASE_PRICE);
      var discountedPrice = basePurchasePrice;
      var finalPrice = discountedPrice;

      // There can be only one discount, affiliate or voucher
      // If affiliate is set, voucher field is not available and voucher code can not be applied.
      if (affiliate && parseInt(affiliate.discount) > 0)
        finalPrice = discountedPrice = applyAffiliateDiscount(basePurchasePrice);
      else if (currentOrder.CurrentOrder.voucherCode)
        finalPrice = discountedPrice = await applyVoucher(basePurchasePrice);

      setSnapshotPrice(discountedPrice.toFixed(2));

      if (currentOrder.CurrentOrder.addAnalysis)
        finalPrice = discountedPrice + parseFloat(MENTAL_CAPACITY_ANALYSIS_PRICE)
      else
        finalPrice = discountedPrice;

      setTotalPrice(finalPrice.toFixed(2))
    }

    calculateOrder();
  }, [])

  const applyVoucher = async (basePurchasePrice: number) => {
    var discountedValue = basePurchasePrice;

    if (currentOrder.CurrentOrder.voucherCode) {
      const voucherRes = await validateVoucher(currentOrder.CurrentOrder.voucherCode);

      if (voucherRes && voucherRes.status === 200 && voucherRes.data) {
        if (voucherRes.data.active) {
          setDiscountApplied(voucherRes.data.isFixedPrice
            ? `(discount £${voucherRes.data.value}) `
            : `(discount ${voucherRes.data.value}%)`);

          discountedValue = voucherRes.data.isFixedPrice
            ? (basePurchasePrice - parseInt(voucherRes.data.value))
            : basePurchasePrice - (basePurchasePrice * parseInt(voucherRes.data.value) / 100)

          if (discountedValue < 0)
            discountedValue = 0;

          return discountedValue;
        }
      }
    }

    return discountedValue;
  }

  const applyAffiliateDiscount = (basePurchasePrice: number) => {
    if (affiliate && parseInt(affiliate.discount) > 0) {
      setDiscountApplied(`(discount ${affiliate.affiliateName} ${affiliate.discount}%)`);

      var discountedValue = basePurchasePrice - (basePurchasePrice * parseInt(affiliate.discount) / 100)

      if (discountedValue < 0)
        discountedValue = 0;

      return discountedValue
    }
    else
      return basePurchasePrice
  }

  const payWithoutSubscription = async (): Promise<PaymentResult> => {

    const paymentIntentData = await createPaymentIntent(
      currentOrder.CurrentOrder.voucherCode,
      currentRecording.VideoRecording!.videoId,
      currentOrder.CurrentOrder.addAnalysis,
      affiliatePrePaidToken);

    // The whole price was covered by a voucher, token, discount or subscription: the backend has
    // recorded the transaction and marked the recording paid, so there is nothing to confirm with Stripe.
    if (paymentIntentData?.paid) {
      return { success: true };
    }

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      console.error('Stripe.js has not loaded yet.');
      return { success: false };
    }

    const clientSecret = paymentIntentData?.paymentIntentSecret;

    if (clientSecret) {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement) as StripeCardElement,
          billing_details: {
            email: await Authorization.GetUsername(),
          },
        },
      });

      if (!(result.paymentIntent && result.paymentIntent.status === "succeeded")) {
        console.error(JSON.stringify(result.paymentIntent));
        console.error(result.error);
        console.error('There is an issue with payment.');
        alert('There is an issue with payment.')
        return { success: false };
      } else {
        return { success: true, paymentIntent: result.paymentIntent };
      }
    }

    console.error('Stripe secret is missing.');
    return { success: false };
  }

  // When initial payment is not 0.00 pounds, payment should be confirmed.
  const confirmCardPayment = async (subscription: any): Promise<PaymentResult> => {
    console.log('confirm Card Payment')

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return { success: false };
    }

    const { client_secret } = subscription.latest_invoice.payment_intent;

    if (!client_secret) {
      alert('There is an issue with payment.')
      return { success: false };
    }

    const result = await stripe.confirmCardPayment(client_secret, {
      payment_method: {
        card: elements.getElement(CardElement) as StripeCardElement,
        billing_details: {
          email: await Authorization.GetUsername(),
        },
      },
    });

    if (result.paymentIntent?.status === 'succeeded') {
      await syncSubscription();
      return { success: true, paymentIntent: result.paymentIntent };
    } else {
      console.log(JSON.stringify(result.paymentIntent));
      console.log(result.error);
      alert('There is an issue with payment.')
    }

    return { success: false };
  }

  // When initial payment is £0.00, only credit card setup should be confirmed.
  const confirmCardSetup = async (subscription: any): Promise<PaymentResult> => {

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return { success: false };
    }

    const { pending_setup_intent } = subscription;

    if (pending_setup_intent) {
      const { client_secret } = subscription.pending_setup_intent;
      var result = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement) as StripeCardElement,
          billing_details: {
            email: await Authorization.GetUsername(),
          },
        }
      });

      if (result.error) {
        console.log('[error]', result.error);
      }

      if (result.setupIntent?.status === 'succeeded') {
        await syncSubscription();
        // No payment was taken, so this is not a purchase.
        return { success: true };
      }
    }

    return { success: false };

  }

  const handlePayment = async (): Promise<PaymentResult> => {
    // free cases:
    // TODO: 30 days free update after initial payment
    // TODO: Active LU subscription free updates
    // 100% voucher without selecting Yes for lifetime updates ???
    // Affiliate with 100% discount

    // payment required
    // initial payment
    // lifetime updates selected
    // voucher does not cover 100%
    // affiliate discount less than 100%
    console.log('handlePayment')

    let subscriptionResponse = null;

    // do subscription payment
    if (currentOrder.CurrentOrder.addLifetimeUpdates) {
      try {
        subscriptionResponse = await paymentSubscription(currentOrder.CurrentOrder.voucherCode, currentRecording.VideoRecording!.videoId);
      } catch (e) {
        console.error(e)
        alert('Please try again later.')
        return { success: false };
      }

      if (subscriptionResponse.data.error) {
        console.error(subscriptionResponse.data.error)
        alert('Please try again later.')
        return { success: false };
      }
      console.log('subscription created')
    }

    // Pay without subscription
    if (subscriptionResponse === null && !currentOrder.CurrentOrder.addLifetimeUpdates) {
      return await payWithoutSubscription();
    }

    if (!subscriptionResponse?.data)
      return { success: false };

    // pay after subscription
    if (subscriptionResponse && subscriptionResponse.data && subscriptionResponse.data.subscription.latest_invoice?.payment_intent?.client_secret != null) {
      return await confirmCardPayment(subscriptionResponse.data.subscription);
    } else {
      return await confirmCardSetup(subscriptionResponse.data.subscription);
    }
  };

  const isFreeOfCharge = () => {
    // NO PAYMENT when there is Affiliate token, voucher 100% or affiliate discount 100% without lifetime updates.
    if (!currentOrder.CurrentOrder.addAnalysis 
        && !currentOrder.CurrentOrder.addLifetimeUpdates) {
      // NO PAYMENT when affiliate token is active
      if (affiliatePrePaidToken)
        return true;

      // NO PAYMENT when voucher discount 100%
      if (currentOrder.CurrentOrder.voucher && currentOrder.CurrentOrder.voucher.isFixedPrice === false && parseInt(currentOrder.CurrentOrder.voucher.value ?? 0) === 100)
        return true;

      // NO PAYMENT when affiliate discount 100%
      if (affiliate && parseInt(affiliate.discount) === 100)
        return true;

      // NO PAYMENT when LU is active
      if (isUserLifetimeSubscriptionActive)
        return true;

      //TODO: NO payment when there is less then 30 days since initial payment

    }

    return false;
  }

  // There can be only one discount, affiliate or voucher (see calculateOrder).
  const getCoupon = () => {
    if (affiliate && parseInt(affiliate.discount) > 0)
      return affiliate.affiliateName;
    return currentOrder.CurrentOrder.voucherCode;
  }

  const renderCardRow = () => {
    if (isFreeOfCharge()) {
      return;
    }

    return (
      <div className="pay-card-row">
        <div className="card-box">
          <CardElement options={{ hidePostalCode: true }} />
        </div>
        <div className="desktop-inline-row">
          <img src="/PoweredbyStripe.svg" alt="PoweredbyStripe" />
          <div className="bold">for secure payments</div>
        </div>
      </div>
    );
  }

  const renderNextLabel = (text: string) => {
    if (confirmingPayment)
      return <span className="confirming-payment" data-testid="confirming-payment"><CircularProgress size={22} /> Confirming your payment…</span>;
    return nextButtonSubmitting ? <CircularProgress /> : text;
  }

  return (
    <form className="CheckOut" onSubmit={next}>
      <div className="bold ft-25 mt-12">Final Step. Complete Your Secure Payment</div>

      <div className='mt-22'>
        <div className="flex-inline accept-condition-check label-box ft-22">
          <div className="label">Order recap:</div>
        </div>

        <div className="flex-inline ft-22">
          <div className="label">Capacity Vault Secure Setup:</div>
          <div className="bold">£{snapshotPrice} {discountApplied}</div>
        </div>
        

{/* 
        <div className="flex-inline ft-22">
          <div className=" bold"></div>
          <div className="bold">{discountApplied}</div>
        </div> */}

        {currentOrder.CurrentOrder.addAnalysis &&
          <div className="flex-inline ft-22">
            <div className="label">Capacity Snapshot Analysis:</div>
            <div className="bold">£{75}</div>
          </div>
        }

        {currentOrder.CurrentOrder.addLifetimeUpdates &&
          <div className="flex-inline ft-22">
            <div className="label">Lifetime Updates: <span className='bold'>free</span> for 1 year <br className='show-on-mobile'></br> then <span className='bold'>£10 annually</span>:</div>
            <div><span className='bold'>£0</span></div>
          </div>
        }

        <div className="flex-inline ft-22">
          <div className="label bold">Amount due:</div>
          <div className="bold">£{totalPrice}</div>
        </div>

        <div className="mt-40">{renderCardRow()}</div>
      </div>

      {paymentPending &&
        <div className="payment-pending ft-22" data-testid="payment-pending">
          <p>Your order went through, but we could not confirm it yet. You do not need to pay again: your certificate will be emailed to you once it is ready.</p>
          <Button
            id='continue-btn'
            variant="contained"
            color="primary"
            className="green-submit-button"
            onClick={() => props.next()}
          >
            Continue
          </Button>
        </div>
      }

      {!paymentPending &&
        <div className='buttons-row'>
          <Button
            id='back-btn'
            variant='outlined'
            color="primary"
            disabled={nextButtonSubmitting}
            className="back-button"
            onClick={back}
          >
            Back
          </Button>

          <Button
            id='next-btn'
            variant="contained"
            color="primary"
            type="submit"
            disabled={nextButtonSubmitting}
            className="green-submit-button"
          >
            {renderNextLabel('Confirm & Pay')}
          </Button>
        </div>
      }
    </form>
  )
};

export default CheckOut;
